/**
 * ONE threshold-aware color band for every OE / REPS score (Beta-1 plan P5-01, P4-21).
 *
 * Tim's rule (Help → Core Concepts): green < 4, yellow 4–6, red > 6 with the default threshold
 * of 6. Generalised to the organization's per-quadrant threshold T:
 *   green   score < T − 2
 *   yellow  T − 2 ≤ score ≤ T
 *   red     score > T          — the same `score > threshold` comparator checkThresholds uses
 *                                for warnings, so colors and warnings can never disagree.
 * Pure module; `scoring.getScoreColor`, `constants.oeBand` and the exports delegate here.
 */

export const BAND_COLORS = Object.freeze({
  healthy: '#2e7d32', // green
  monitor: '#f9a825', // yellow
  warning: '#c62828', // red
  unknown: '#9e9e9e',
});

export const BAND_LABELS = Object.freeze({ healthy: 'Healthy', monitor: 'Monitor', warning: 'Warning', unknown: '—' });

export const DEFAULT_THRESHOLD = 6;
const QUADRANTS = ['reliability', 'efficiency', 'productivity', 'safety'];
const SHORT = { R: 'reliability', E: 'efficiency', P: 'productivity', S: 'safety' };

/**
 * Accept every thresholds shape that exists in the data ({R,E,P,S}, {reliability,…}, numbers as
 * strings, partial objects) and return the canonical `{ reliability, efficiency, productivity, safety }`.
 */
export function normalizeThresholds(input) {
  const out = { reliability: DEFAULT_THRESHOLD, efficiency: DEFAULT_THRESHOLD, productivity: DEFAULT_THRESHOLD, safety: DEFAULT_THRESHOLD };
  if (!input || typeof input !== 'object') return out;
  for (const [k, v] of Object.entries(input)) {
    const key = QUADRANTS.includes(k) ? k : SHORT[k];
    const n = Number(v);
    if (key && Number.isFinite(n) && n > 0) out[key] = n;
  }
  return out;
}

/** Threshold for one quadrant from any thresholds shape (or a plain number). */
export function thresholdFor(thresholds, quadrant) {
  if (typeof thresholds === 'number') return thresholds;
  return normalizeThresholds(thresholds)[quadrant] ?? DEFAULT_THRESHOLD;
}

/** Band key for a score against a threshold: 'healthy' | 'monitor' | 'warning' | 'unknown'. */
export function bandKey(score, threshold = DEFAULT_THRESHOLD) {
  const s = Number(score);
  const t = Number.isFinite(Number(threshold)) ? Number(threshold) : DEFAULT_THRESHOLD;
  if (score == null || Number.isNaN(s)) return 'unknown';
  if (s > t) return 'warning';
  if (s >= t - 2) return 'monitor';
  return 'healthy';
}

/** `{ key, label, color, threshold }` for a score. `threshold` may be a number or a thresholds object + quadrant. */
export function scoreBand(score, threshold = DEFAULT_THRESHOLD, quadrant = null) {
  const t = typeof threshold === 'object' && threshold !== null ? thresholdFor(threshold, quadrant || 'reliability') : threshold;
  const key = bandKey(score, t);
  return { key, label: BAND_LABELS[key], color: BAND_COLORS[key], threshold: Number(t) || DEFAULT_THRESHOLD };
}

export function scoreColor(score, threshold = DEFAULT_THRESHOLD, quadrant = null) {
  return scoreBand(score, threshold, quadrant).color;
}

/** The "< 4 / 4–6 / > 6" legend text for a threshold (REPSQuadrant tooltip, reports). */
export function bandLegend(threshold = DEFAULT_THRESHOLD) {
  const t = Number(threshold) || DEFAULT_THRESHOLD;
  const lo = t - 2;
  return { healthy: `< ${lo}`, monitor: `${lo}–${t}`, warning: `> ${t}` };
}
